/* eslint-disable import/no-restricted-paths */
import { HostImageInteractor, type IImageMastRepository, type ImageMast } from "@CHILLNN-Inc/chillnn-abr-core";

import { graphql } from "@/lib/gql/__generated__";
import type { ImageInput } from "@/lib/gql/__generated__/graphql";

import type { ApolloClient } from "@apollo/client";

export class ImageMastRepository implements IImageMastRepository {
	constructor(private apolloClient: ApolloClient<object>) {}

	public async add(input: ImageMast): Promise<ImageMast> {
		const ImageUpload_ImageMastRepository_Mutation = graphql(`
			mutation ImageUpload_ImageMastRepository($input: ImageInput!) {
				imageUpload(input: $input) {
					userErrors
					imageList {
						id
						list {
							ownerID
							uuid
							image {
								bucket
								key
								region
								mimeType
								fileName
							}
							createdAt
							deletedAt
						}
					}
				}
			}
		`);

		const imageInput: ImageInput = {
			ownerID: input.ownerID,
			image: {
				bucket: input.image.bucket,
				key: input.image.key,
				region: input.image.region,
				mimeType: input.image.mimeType,
				fileName: input.image.fileName,
			},
		} satisfies ImageInput;

		const { data } = await this.apolloClient.mutate({
			mutation: ImageUpload_ImageMastRepository_Mutation,
			variables: {
				input: imageInput,
			},
		});

		if (!data?.imageUpload.imageList) {
			throw new Error("Failed to upload image");
		}

		return data.imageUpload.imageList.list[0];
	}

	public async delete(ownerID: string, uuid: string): Promise<ImageMast> {
		const ImageDelete_ImageMastRepository_Mutation = graphql(`
			mutation ImageDelete_ImageMastRepository($ownerID: ID!, $uuid: ID!) {
				imageDelete(ownerID: $ownerID, uuid: $uuid) {
					userErrors
					imageList {
						id
						list {
							ownerID
							uuid
							image {
								bucket
								key
								region
								mimeType
								fileName
							}
							createdAt
							deletedAt
						}
					}
				}
			}
		`);

		const { data } = await this.apolloClient.mutate({
			mutation: ImageDelete_ImageMastRepository_Mutation,
			variables: {
				ownerID,
				uuid,
			},
		});

		if (!data?.imageDelete.imageList) {
			throw new Error("Failed to delete image");
		}

		return data.imageDelete.imageList.list[0];
	}

	public async fetchImages(ownerID: string): Promise<ImageMast[]> {
		const ImageList_ImageMastRepository_Query = graphql(`
			query ImageList_ImageMastRepository($ownerID: ID!) {
				imageList(ownerID: $ownerID) {
					id
					list {
						ownerID
						uuid
						image {
							bucket
							key
							region
							mimeType
							fileName
						}
						createdAt
						deletedAt
					}
				}
			}
		`);

		const { data } = await this.apolloClient.query({
			query: ImageList_ImageMastRepository_Query,
			variables: {
				ownerID,
			},
		});

		return data.imageList.list.map((item) => {
			return {
				ownerID: item.ownerID,
				uuid: item.uuid,
				image: item.image,
				createdAt: item.createdAt,
				deletedAt: item.deletedAt,
			} satisfies ImageMast;
		});
	}
}

export const imageMastRepository = (apolloClient: ApolloClient<object>) => new ImageMastRepository(apolloClient);

export const imageMastInteractor = (apolloClient: ApolloClient<object>) =>
	new HostImageInteractor(imageMastRepository(apolloClient));
